import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useI18n, useLogger, useSelector } from '~Hooks';
import StepFrame from '../../Scenes/Checkout/StepFrame';
import { useExpressOrder } from '../../Providers/ExpressOrderProvider';
import { useExpressLoyaltyIdentifying } from '../../Providers/ExpressLoyaltyProvider';
import Countdown from './Countdown';
import Modal, { MODAL_TRANSITION_MS } from '../Modal';

/** Total idle time before the flow resets. Same as the kiosk. */
const TIMEOUT_IN_MS = 30000;

/** Seconds left when the warning comes up. Same as the kiosk. */
const TIMEOUT_WARN_SECONDS = 8;

/** Same entrance/exit pattern as ClearCartConfirm: keyframe in, CSSTransition-driven out. */
/** The card: scales and fades in, and back out. StepFrame provides its own padding. */
const Card = styled.div`
  position: relative;
  width: min(100%, 860px);
  border-radius: 36px;
  background: var(--eposSurface);
  color: var(--eposText);
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.45);
  transform: translate3d(0, 0, 0) scale(1);
  opacity: 1;
  animation: eposIdleCardIn ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity ${MODAL_TRANSITION_MS}ms ease-out;
  will-change: transform, opacity;

  .epos-idle-timeout-exit-active &,
  .epos-idle-timeout-exit-done & {
    animation: none;
    transform: translate3d(0, 24px, 0) scale(0.96);
    opacity: 0;
  }

  @keyframes eposIdleCardIn {
    from {
      transform: translate3d(0, 24px, 0) scale(0.96);
      opacity: 0;
    }
    to {
      transform: translate3d(0, 0, 0) scale(1);
      opacity: 1;
    }
  }
`;

interface Props {
  /** Only counts while the shopper is past the welcome screen. */
  active: boolean;
  /** The idle time ran out - EposFlow empties the cart and goes back to welcome. */
  onTimeout: () => void;
}

/**
 * The kiosk's idle guard, in EPOS clothes. Any touch bumps `timeOfLastAction`
 * in Redux, which restarts the clock and drops the warning; without one the
 * warning comes up for the last seconds and then the flow resets. Paused
 * while the terminal has the order, exactly like the kiosk's `hasPayment`,
 * and while it is asking for a loyalty card - taps on the terminal never reach Redux.
 */
const IdleTimeout: FC<Props> = ({ active, onTimeout }) => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-idle-timeout');
  const expressOrder = useExpressOrder();
  const timeOfLastAction = useSelector((state) => state.timeOfLastAction);
  /** A card prompt is a wait on the terminal, not idleness - the clock restarts once it ends. */
  const identifyingLoyalty = useExpressLoyaltyIdentifying();

  const [warningVisible, setWarningVisible] = useState(false);
  const warnTimerRef = useRef<ReturnType<typeof setTimeout>>();

  /** The terminal is busy or the order is done - never reset under it. */
  const paying = expressOrder.phase === 'paying' || expressOrder.phase === 'done';

  // Every touch moves timeOfLastAction: hide the warning and start the clock over.
  useEffect(() => {
    clearTimeout(warnTimerRef.current);
    setWarningVisible(false);

    if (!active || !timeOfLastAction) {
      return undefined;
    }

    if (identifyingLoyalty) {
      logger.info('Idle clock held while the terminal asks for a loyalty card');
      return undefined;
    }

    warnTimerRef.current = setTimeout(() => {
      logger.info('Idle warning shown', { phase: expressOrder.phase });
      setWarningVisible(true);
    }, TIMEOUT_IN_MS - TIMEOUT_WARN_SECONDS * 1000);

    return () => clearTimeout(warnTimerRef.current);
  }, [active, timeOfLastAction, identifyingLoyalty]);

  const handleExpire = useCallback(() => {
    logger.info('Idle timeout reached - resetting the flow', { phase: expressOrder.phase });
    setWarningVisible(false);
    // No-op while paying or done, so the order is only dropped when it is safe to.
    expressOrder.cancel();
    onTimeout();
  }, [expressOrder.cancel, expressOrder.phase, onTimeout]);

  /** A tap on the backdrop is a touch like any other; hide right away rather than waiting for Redux. */
  const handleTouch = useCallback(() => setWarningVisible(false), []);

  return (
    <Modal
      open={warningVisible && !paying}
      exitPrefix="epos-idle-timeout"
      onBackdropTap={handleTouch}
      zIndex={30}
      testId="epos-idle-timeout">
      <Card>
        <StepFrame
          testId="epos-idle-timeout-step"
          title={i18n('SSK.Timeout.Header')}
          subtitle={i18n('SSK.Timeout.Subheader')}>
          <Countdown from={TIMEOUT_WARN_SECONDS} paused={paying} onExpire={handleExpire} />
        </StepFrame>
      </Card>
    </Modal>
  );
};

export default memo(IdleTimeout);
